import { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsType } from 'echarts';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../lib/api';

export function MapsChart({ theme }: { theme: 'light' | 'dark' }): JSX.Element {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<EChartsType | null>(null);
  const { data: mapsResponse, isLoading } = useQuery({
    queryKey: ['maps-usage'],
    queryFn: () => apiFetch<{ labels: string[]; values: number[] }>('/maps-usage/'),
  });
  const labels = useMemo(() => mapsResponse?.labels ?? [], [mapsResponse]);
  const values = useMemo(() => mapsResponse?.values ?? [], [mapsResponse]);

  useEffect(() => {
    if (!chartRef.current) return;
    chartInstance.current?.dispose();
    const instance = echarts.init(chartRef.current);
    chartInstance.current = instance;

    const resizeHandler = () => instance.resize();
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      instance.dispose();
    };
  }, [theme]);

  useEffect(() => {
    if (!chartInstance.current) return;

    const styles = getComputedStyle(document.documentElement);
    const toHsl = (token: string) => `hsl(${styles.getPropertyValue(token).trim()})`;

    const axisColor = toHsl('--border');
    const textColor = toHsl('--muted-foreground');
    const barColor = toHsl('--primary');
    const cardColor = toHsl('--card');

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: cardColor,
        borderColor: axisColor,
        textStyle: { color: toHsl('--foreground') },
        axisPointer: { type: 'shadow' },
      },
      grid: { left: 48, right: 16, top: 16, bottom: 72 },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: axisColor } },
        axisLabel: { color: textColor, rotate: 45 },
        nameTextStyle: { color: textColor },
      },
      yAxis: {
        type: 'value',
        name: 'Кол-во игр',
        axisLine: { lineStyle: { color: axisColor } },
        splitLine: { lineStyle: { color: axisColor, type: 'dashed' } },
        axisLabel: { color: textColor },
        nameTextStyle: { color: textColor },
      },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth: 18,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(255,222,0,0.9)' },
              { offset: 1, color: 'rgba(255,222,0,0.18)' },
            ]),
            borderColor: barColor,
            borderWidth: 1,
          },
          emphasis: { focus: 'series' },
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [theme, labels, values]);

  return (
    <div className="card p-4 space-y-4">
      <div>
        <p className="text-2xl font-semibold">Карты</p>
        <p className="text-sm text-muted-foreground">Частота использования карт</p>
      </div>
      <motion.div
        className="relative h-[360px]"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div ref={chartRef} className="h-full w-full" />
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-end gap-2">
              {[0, 1, 2, 3].map((index) => (
                <motion.div
                  key={index}
                  className="w-2 rounded-sm bg-primary/70"
                  animate={{ height: [12, 36, 16] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: index * 0.15 }}
                />
              ))}
            </div>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}
